import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';

import { showSuccessSnackbar } from 'modules/snackbar';
import { paramConverter } from 'modules/common';
import { dailyStatistics, dailyParGraph } from 'lib/api/time';
import { tableHeaders } from 'lib/api/common';
import { setPortal } from 'modules/portal';
import {
  makeRowColumn,
  getHeadObj,
  dataConverter,
  makeHeadData,
  dayColor,
  converParameters,
  getHost,
} from 'lib/commonLib';

import NewButton from 'components/molecules/NewButton';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import ShoppingCell from 'components/molecules/MbTable/ShoppingCell';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import BarGraph from 'components/organisms/Chart/BarGraph';

import StatisticsPieGraph from './StatisticsPieGraph';

const Statistics = ({ location }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({ portal: portal }));
  const [initialization, setInitialization] = useState(false);
  let initParams = {
    pltfomTpCode: 'all',
    itlTpCode: '01,02,98,99',
    sort: 'desc',
    order: 'STATS_DTTM',
    sDate: moment().format('YYYYMM01'),
    eDate: moment().format('YYYYMMDD'),
    limit: 0,
  };

  // 통계보고서 - 어뷰징관리 - 전환율: adverId, eDate, isPop, popGroup, sDate, uri
  // 통계보고서 - 어뷰징관리 - 평균구매액: adverId, sDate, eDate, isPop,uri
  // 키워드센터 - 광고주별수집현황 : adverId
  // 통계보고서 - 광고주별통계 - 이슈 : adverId, uri, isPop
  const convertKeys = [
    'adverId',
    'mediaScriptNo',
    'advrtsPrdtCode',
    'advrtsTpCode',
    'pltfomTpCode',
    'sDate',
    'eDate',
    'uri',
  ];
  const newInitParams = dataConverter(
    queryString.parse(location.search),
    convertKeys
  );
  initParams = { ...initParams, ...newInitParams };
  const isPopPage = !!queryString.parse(location.search)['isPop'];
  const [params, setParams] = useState(initParams);
  const [tempParams, setTempParams] = useState(initParams);
  const [loading, setLoading] = useState(false);
  const initTableList = { total: [], list: [] };
  const [tableList, setTableList] = useState(initTableList);
  const [tableHead, setTableHead] = useState([]);

  const graphList = [
    {
      title: t('지출금액'),
      data: {
        bar: ['ADVRTS_AMT', 'SHOP_CTGR_ADVRTS_AMT'],
        line: ['ADVER_CNT'],
      },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { ADVRTS_AMT: true, SHOP_CTGR_ADVRTS_AMT: true },
      },
      referenceLine: {
        ADVRTS_AMT: {
          type: 'average',
          label: t('지출금액'),
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        SHOP_CTGR_ADVRTS_AMT: {
          xAxisId: 1,
          keyLabel: t('지출금액(쇼핑)'),
          color: theme.palette.common.black,
        },
      },
    },
    {
      title: t('예산'),
      data: { bar: ['CAMP_BDGT_AMT', 'ADVRTS_AMT'] },
      yAxisData: { left: { CAMP_BDGT_AMT: true }, right: { ADVRTS_AMT: true } },
      referenceLine: {
        CAMP_BDGT_AMT: {
          type: 'average',
          label: t('캠페인 예산'),
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: { CAMP_BDGT_AMT: { xAxisId: 1 } },
    },
    {
      title: t('노출'),
      data: { bar: ['PAR_EPRS_CNT'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { PAR_EPRS_CNT: true },
      },
      referenceLine: {
        PAR_EPRS_CNT: {
          type: 'average',
          label: t('노출'),
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: { PAR_EPRS_CNT: { color: theme.palette.common.green } },
    },
    {
      title: t('미노출'),
      data: { bar: ['NO_PAR_EPRS_CNT'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { NO_PAR_EPRS_CNT: true },
      },
      referenceLine: {
        NO_PAR_EPRS_CNT: {
          type: 'average',
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: { NO_PAR_EPRS_CNT: { color: theme.palette.common.green } },
    },
    {
      title: t('클릭수'),
      data: { bar: ['CLICK_CNT'], line: ['ADVER_CNT'] },
      yAxisData: { left: { ADVER_CNT: true }, right: { CLICK_CNT: true } },
      referenceLine: {
        CLICK_CNT: { type: 'average', visibleValue: true, color: '#f66172' },
      },
      customizeData: { CLICK_CNT: { color: theme.palette.common.green } },
    },
    {
      title: 'CTR',
      data: { bar: ['CLICK_RATE'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { CLICK_RATE: true, isPercent: true },
      },
      referenceLine: {
        ADVER_CNT: { type: 'average', visibleValue: true, color: '#f66172' },
      },
      customizeData: {
        CLICK_RATE: {
          tooltipFormat: '0.000',
          color: theme.palette.common.green,
          isPercent: true,
        },
      },
    },
    {
      title: '평균CPC',
      data: { bar: ['AVG_ADVRTS_AMT'], line: ['ADVER_CNT'] },
      yAxisData: { left: { ADVER_CNT: true }, right: { AVG_ADVRTS_AMT: true } },
      referenceLine: {
        AVG_ADVRTS_AMT: {
          type: 'average',
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        AVG_ADVRTS_AMT: {
          tooltipFormat: '0,000.0',
          color: theme.palette.common.green,
        },
      },
    },
    {
      title: 'ECPM',
      data: { bar: ['ECPM'], line: ['ADVER_CNT'] },
      yAxisData: { left: { ADVER_CNT: true }, right: { ECPM: true } },
      referenceLine: {
        ECPM: { type: 'average', visibleValue: true, color: '#f66172' },
      },
      customizeData: {
        ECPM: { tooltipFormat: '0,000.0', color: theme.palette.common.green },
      },
    },
    {
      title: t('세션ROAS'),
      data: { bar: ['SESSION_ROAS'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { SESSION_ROAS: true, isPercent: true },
      },
      referenceLine: {
        SESSION_ROAS: {
          type: 'average',
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        SESSION_ROAS: {
          isPercent: true,
          tooltipFormat: '0,000.0',
          color: theme.palette.common.green,
        },
      },
    },
    {
      title: t('결제ROAS'),
      data: { bar: ['MOB_SESSION_ROAS'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { MOB_SESSION_ROAS: true, isPercent: true },
      },
      referenceLine: {
        MOB_SESSION_ROAS: {
          type: 'average',
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        MOB_SESSION_ROAS: {
          isPercent: true,
          tooltipFormat: '0,000.0',
          color: theme.palette.common.green,
        },
      },
    },
    {
      title: t('직접ROAS'),
      data: { bar: ['DIRECT_ROAS'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { DIRECT_ROAS: true, isPercent: true },
      },
      referenceLine: {
        DIRECT_ROAS: {
          type: 'average',
          label: t('직접ROAS'),
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        DIRECT_ROAS: {
          isPercent: true,
          tooltipFormat: '0,000.0',
          color: theme.palette.common.green,
        },
      },
    },
    {
      title: t('총ROAS'),
      data: { bar: ['ROAS'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { ROAS: true, isPercent: true },
      },
      referenceLine: {
        ROAS: { type: 'average', visibleValue: true, color: '#f66172' },
      },
      customizeData: {
        ROAS: {
          isPercent: true,
          tooltipFormat: '0,000.0',
          color: theme.palette.common.green,
        },
      },
    },
    {
      title: t('쇼핑ROAS'),
      data: { bar: ['TRGT_SESSION_ROAS'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { TRGT_SESSION_ROAS: true, isPercent: true },
      },
      referenceLine: {
        TRGT_SESSION_ROAS: {
          type: 'average',
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        TRGT_SESSION_ROAS: {
          isPercent: true,
          tooltipFormat: '0,000.0',
          color: theme.palette.common.green,
        },
      },
    },
    {
      title: t('지면수'),
      data: { bar: ['PAR_CNT'], line: ['ADVER_CNT'] },
      yAxisData: { left: { ADVER_CNT: true }, right: { PAR_CNT: true } },
      referenceLine: {
        PAR_CNT: { type: 'average', visibleValue: true, color: '#f66172' },
      },
      customizeData: { PAR_CNT: { color: theme.palette.common.green } },
    },
    {
      title: t('지면수_3000'),
      data: { bar: ['T_AMT_PAR_CNT'], line: ['ADVER_CNT'] },
      yAxisData: { left: { ADVER_CNT: true }, right: { T_AMT_PAR_CNT: true } },
      referenceLine: {
        T_AMT_PAR_CNT: {
          type: 'average',
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: { T_AMT_PAR_CNT: { color: theme.palette.common.green } },
    },
    {
      title: t('지면수_500/데이터안맞음'),
      data: { bar: ['F_AMT_PAR_CNT'], line: ['ADVER_CNT'] },
      yAxisData: { left: { ADVER_CNT: true }, right: { F_AMT_PAR_CNT: true } },
      referenceLine: {
        F_AMT_PAR_CNT: {
          type: 'average',
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: { F_AMT_PAR_CNT: { color: theme.palette.common.green } },
    },
    {
      title: t('요청수/게재률'),
      data: { bar: ['REQ_CNT'], line: ['ENT_RATE'] },
      yAxisData: {
        left: { ENT_RATE: true, isPercent: true },
        right: { REQ_CNT: true },
      },
      referenceLine: {
        ENT_RATE: {
          type: 'average',
          label: t('게재률'),
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        REQ_CNT: { color: theme.palette.common.green },
        ENT_RATE: { tooltipFormat: '0.0', isPercent: true },
      },
    },
    {
      title: t('TC/CW점유율'),
      data: { line: ['TC_OCC_RATE', 'CW_OCC_RATE'] },
      yAxisData: {
        left: { TC_OCC_RATE: true, isPercent: true },
        right: { CW_OCC_RATE: true, isPercent: true },
      },
      referenceLine: {
        TC_OCC_RATE: {
          type: 'average',
          label: t('TC점유율'),
          visibleValue: true,
          color: theme.palette.graph.key.TC,
        },
        CW_OCC_RATE: {
          type: 'average',
          label: t('CW점유율'),
          visibleValue: true,
          color: theme.palette.graph.key.CW,
        },
      },
      customizeData: {
        TC_OCC_RATE: {
          tooltipFormat: '0.0',
          isPercent: true,
          color: theme.palette.graph.key.TC,
        },
        CW_OCC_RATE: {
          tooltipFormat: '0.0',
          isPercent: true,
          color: theme.palette.graph.key.CW,
        },
      },
    },
    {
      title: t('CW/SR점유율'),
      data: { line: ['SR_OCC_RATE', 'CW_OCC_RATE'] },
      yAxisData: {
        left: { SR_OCC_RATE: true, isPercent: true },
        right: { CW_OCC_RATE: true, isPercent: true },
      },
      referenceLine: {
        SR_OCC_RATE: {
          type: 'average',
          label: t('본상품'),
          visibleValue: true,
          color: '#f66172',
        },
        CW_OCC_RATE: {
          type: 'average',
          label: t('카트매칭'),
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        CW_OCC_RATE: {
          color: theme.palette.graph.key.CW,
          tooltipFormat: '0.0',
          isPercent: true,
          visibleValue: true,
        },
        SR_OCC_RATE: {
          color: theme.palette.graph.key.SR,
          tooltipFormat: '0.0',
          isPercent: true,
          visibleValue: true,
        },
      },
    },
    {
      title: t('상품타겟팅 점유율'),
      data: { bar: ['PRDT_TRGT_OCC_RATE'], line: ['ADVER_CNT'] },
      yAxisData: {
        left: { ADVER_CNT: true },
        right: { PRDT_TRGT_OCC_RATE: true, isPercent: true },
      },
      referenceLine: {
        PRDT_TRGT_OCC_RATE: {
          type: 'average',
          label: t('상품타겟팅 점유율'),
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        PRDT_TRGT_OCC_RATE: {
          isPercent: true,
          tooltipFormat: '0,000.0',
          color: theme.palette.common.green,
        },
      },
    },
    {
      title: t('쇼핑카테고리 점유율'),
      data: { bar: ['SHOP_CTGR_OCC_RATE'], line: ['SHOP_CTGR_ADVER_CNT'] },
      yAxisData: {
        left: { SHOP_CTGR_ADVER_CNT: true },
        right: { SHOP_CTGR_OCC_RATE: true, isPercent: true },
      },
      referenceLine: {
        SHOP_CTGR_OCC_RATE: {
          type: 'average',
          label: t('쇼핑카테고리 점유율'),
          visibleValue: true,
          color: '#f66172',
        },
      },
      customizeData: {
        SHOP_CTGR_OCC_RATE: {
          isPercent: true,
          tooltipFormat: '0,000.0',
          color: theme.palette.common.green,
        },
      },
    },
    {
      title: t('상위 매체'),
      customizeData: {
        link: converParameters({
          url: getHost('new') + '/report/daily/topMedia',
          sDate: params.sDate,
          eDate: params.eDate,
          isPop: 1,
        }),
      },
    },
    {
      title: t('상위카테고리'),
      customizeData: {
        link: converParameters({
          ...params,
          url: getHost('new') + '/report/daily/topCategory',
          isPop: 1,
        }),
      },
    },
    {
      title: t('13개월 분석표'),
      customizeData: {
        link: converParameters({
          // ...params,
          url: getHost('new') + '/report/daily/longTime',
          sDate: moment(params.eDate, 'YYYYMMDD')
            .add(-365, 'day')
            .format('YYYYMMDD'),
          eDate: params['eDate'],
          isPop: 1,
        }),
      },
    },
  ];

  const toolTip = (
    <>
      1. {t('클릭,소진이 1이상 발생한 광고주 수')}
      <br />
      2. {t('노출,클릭,컨버전 중 데이터가 1이상 발생한 광고주 수')}
      <br />
      3. {t('라이브중인 광고주 수(=세팅수))')}
    </>
  );

  useEffect(() => {
    if (!isPopPage) {
      tableHeaders({ url: '/report/daily/par' })
        .then((response) => {
          setTableHead(response.data.data);
        })
        .catch((error) => {
          handleClickSnackbar({ message: error, option: { type: 'error' } });
        });
    } // eslint-disable-next-line
  }, []);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  useEffect(() => {
    if (!isPopPage) {
      setLoading(true);
      setTableList(initTableList);
      dailyStatistics({ params: paramConverter(params) })
        .then((response) => {
          const { data } = response.data;
          let newlist = data;
          let newTotal = [];
          if (data[0]['STATS_DTTM'] === 'total') {
            newTotal = data[0];
            newlist.shift();
          }
          setTableList({ ...tableList, total: newTotal, list: newlist });
        })
        .catch((error) => {
          handleClickSnackbar({ message: error, option: { type: 'error' } });
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line
  }, [params]);

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (
      JSON.stringify(params) !== JSON.stringify(initParams) ||
      JSON.stringify(tempParams) !== JSON.stringify(initParams)
    ) {
      setInitialization(true);
      setParams(initParams);
      setTempParams(initParams);
    }
  };

  const getParam = () => {
    if (JSON.stringify(params) !== JSON.stringify(tempParams)) {
      setParams(tempParams);
    }
  };
  const getTempParam = (newParams) => {
    if (JSON.stringify(tempParams) !== JSON.stringify(newParams)) {
      setTempParams(newParams);
    }
  };

  const customized = {
    STATS_DTTM: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'STATS_DTTM');
        const showWindowUrl = converParameters({
          ...params,
          url: getHost('new') + '/report/media',
          sDate: rowObj['STATS_DTTM'],
          eDate: rowObj['STATS_DTTM'],
        });
        return !!rowObj['PLTFOM_TP_CODE'] ? (
          makeRowColumn(rowObj['PLTFOM_TP_CODE']) === '01' ? (
            t('웹')
          ) : (
            makeRowColumn(rowObj['PLTFOM_TP_CODE']) === '02' && t('모바일')
          )
        ) : (
          <IconAreaWrap
            text={dayColor(
              rowObj['STATS_DTTM'],
              bodyItem1.type,
              bodyItem1.format
            )}
            icon={rowObj['STATS_DTTM'] !== 'total' && 'faExternalLink'}
            toolTipTitle={t('해당 날짜의 매체별 통계')}
            onClick={() => window.open(showWindowUrl, '_blank')}
          />
        );
      },
    },
    ADVER_CNT: {
      makeHead: (headsMap) => {
        return (
          <InfoTitleArea
            title={getHeadObj(headsMap, 'ADVER_CNT').label}
            tooltipTitle={toolTip}
            iconType="fainfoCircle"
          />
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ADVER_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'TOT_ADVER_CNT');
        const bodyItem3 = getHeadObj(headsMap, 'CAMP_ADVER_CNT');
        return (
          !rowObj.isChild &&
          rowObj['STATS_DTTM'] !== 'total' && (
            <>
              {makeRowColumn(
                rowObj['ADVER_CNT'],
                bodyItem1.type,
                bodyItem1.format
              )}
              <br />
              <div className="black">
                {makeRowColumn(
                  rowObj['TOT_ADVER_CNT'],
                  bodyItem2.type,
                  bodyItem2.format
                )}
                <br />(
                {makeRowColumn(
                  rowObj['CAMP_ADVER_CNT'],
                  bodyItem3.type,
                  bodyItem3.format
                )}
                )
              </div>
            </>
          )
        );
      },
    },
    TOT_ADVER_CNT: { merged: true },
    CAMP_ADVER_CNT: { merged: true },
    PAR_EPRS_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'PAR_EPRS_CNT').label}
            <br />({getHeadObj(headsMap, 'REQ_CNT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'PAR_EPRS_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'REQ_CNT');
        return (
          <>
            {makeRowColumn(
              rowObj['PAR_EPRS_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            <br />
            <div className="black">
              (
              {makeRowColumn(
                rowObj['REQ_CNT'],
                bodyItem2.type,
                bodyItem2.format
              )}
              )
            </div>
          </>
        );
      },
    },
    REQ_CNT: { merged: true },
    NO_PAR_EPRS_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'NO_PAR_EPRS_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'AD_OFF_EX_NO_PAR_EPRS_CNT');
        const bodyItem3 = getHeadObj(headsMap, 'GRADE_NO_PAR_EPRS_CNT');
        const showWindowUrl = converParameters({
          ...params,
          url: getHost('new') + '/report/media/nobanner',
          sDate: rowObj['STATS_DTTM'],
          eDate: rowObj['STATS_DTTM'],
          isPop: 1,
        });
        const newText = (
          <>
            {makeRowColumn(
              rowObj['NO_PAR_EPRS_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            {!rowObj.isChild && (
              <>
                <br />(
                {makeRowColumn(
                  rowObj['AD_OFF_EX_NO_PAR_EPRS_CNT'],
                  bodyItem2.type,
                  bodyItem2.format
                )}
                )
                <br />(
                {makeRowColumn(
                  rowObj['GRADE_NO_PAR_EPRS_CNT'],
                  bodyItem3.type,
                  bodyItem3.format
                )}
                )
              </>
            )}
          </>
        );
        return (
          rowObj['STATS_DTTM'] !== 'total' &&
          rowObj['PLTFOM_TP_CODE'] === undefined && (
            <IconAreaWrap
              text={newText}
              icon={'faExternalLink'}
              toolTipTitle={t('해당 날짜의 매체별 미노출 통계')}
              onClick={(e) => handlePortalUrl(showWindowUrl)}
            />
          )
        );
      },
    },
    AD_OFF_EX_NO_PAR_EPRS_CNT: { merged: true },
    GRADE_NO_PAR_EPRS_CNT: { merged: true },
    RES_RATE: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'RES_RATE').label}
            <br />({getHeadObj(headsMap, 'ENT_RATE').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RES_RATE');
        const bodyItem2 = getHeadObj(headsMap, 'ENT_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['RES_RATE'],
              bodyItem1.type,
              bodyItem1.format
            )}
            <br />(
            {makeRowColumn(
              rowObj['ENT_RATE'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      orderFlag: false,
    },
    ENT_RATE: { merged: true },
    CLICK_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CLICK_CNT');
        const showWindowUrl = converParameters({
          ...params,
          url: getHost('new') + '/report/adver/statistics',
          sDate: rowObj['STATS_DTTM'],
          eDate: rowObj['STATS_DTTM'],
          isPop: 1,
          popGroup: 'adver',
        });
        return (
          <IconAreaWrap
            text={makeRowColumn(
              rowObj['CLICK_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            icon={
              rowObj['STATS_DTTM'] !== 'total' &&
              rowObj['PLTFOM_TP_CODE'] === undefined &&
              'faExternalLink'
            }
            toolTipTitle={t('광고주별 통계')}
            onClick={(e) => handlePortalUrl(showWindowUrl)}
          />
        );
      },
    },
    CAMP_BDGT_AMT: { merged: true },
    ADVRTS_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'CAMP_BDGT_AMT').label}
            <br />
            {getHeadObj(headsMap, 'ADVRTS_AMT').label}
            <br />({getHeadObj(headsMap, 'TRGT_ADVRTS_AMT').label})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CAMP_BDGT_AMT');
        const bodyItem2 = getHeadObj(headsMap, 'ADVRTS_AMT');
        const bodyItem3 = getHeadObj(headsMap, 'TRGT_ADVRTS_AMT');
        return (
          <>
            {!rowObj.isChild && (
              <>
                {makeRowColumn(
                  rowObj['CAMP_BDGT_AMT'],
                  bodyItem1.type,
                  bodyItem1.format
                )}
                <br />
              </>
            )}
            {makeRowColumn(
              rowObj['ADVRTS_AMT'],
              bodyItem2.type,
              bodyItem2.format
            )}
            <br />
            <div className={'black'}>
              (
              {makeRowColumn(
                rowObj['TRGT_ADVRTS_AMT'],
                bodyItem3.type,
                bodyItem3.format
              )}
              )
            </div>
          </>
        );
      },
      orderFlag: false,
    },
    TRGT_ADVRTS_AMT: { merged: true },
    AVG_ADVRTS_AMT: {
      makeHead: (headsMap) => {
        return (
          getHeadObj(headsMap, 'AVG_ADVRTS_AMT').label +
          '(' +
          t('평균') +
          'CPC)'
        );
      },
    },
    PAR_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'PAR_CNT');
        return (
          !rowObj.isChild &&
          rowObj['STATS_DTTM'] !== 'total' &&
          makeRowColumn(rowObj['PAR_CNT'], bodyItem1.type, bodyItem1.format)
        );
      },
    },
    RETRN_RATE: {
      makeHead: (headsMap) => {
        return (
          <>
            <InfoTitleArea
              title={getHeadObj(headsMap, 'RETRN_RATE').label}
              tooltipTitle={t(
                '광고 클릭 후 랜딩페이지 창을 7초 이내에 닫은 비율'
              )}
              iconType="fainfoCircle"
            />
            <InfoTitleArea
              title={getHeadObj(headsMap, 'AVAL_RATE').label}
              tooltipTitle={t(
                '광고 클릭 후 랜딩페이지 창을 20초 이후에 닫은 비율'
              )}
              iconType="fainfoCircle"
            />
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'RETRN_RATE');
        const bodyItem2 = getHeadObj(headsMap, 'AVAL_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['RETRN_RATE'],
              bodyItem1.type,
              bodyItem1.format
            )}
            <br />
            <span className="black">
              {makeRowColumn(
                rowObj['AVAL_RATE'],
                bodyItem2.type,
                bodyItem2.format
              )}
            </span>
          </>
        );
      },
      orderFlag: false,
    },
    AVAL_RATE: { merged: true },
    ORDER_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'ORDER_CNT').label} <br />({t('쇼핑')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ORDER_CNT');
        const bodyItem2 = getHeadObj(headsMap, 'TRGT_ORDER_CNT');
        const showWindowUrl = converParameters({
          ...params,
          url: getHost('new') + '/report/conversion/state_list',
          sDate: rowObj['STATS_DTTM'],
          eDate: rowObj['STATS_DTTM'],
          isPop: 1,
        });
        const text = (
          <>
            {makeRowColumn(
              rowObj['ORDER_CNT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            <br />(
            {makeRowColumn(
              rowObj['TRGT_ORDER_CNT'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
        return (
          <IconAreaWrap
            text={text}
            icon={
              rowObj['STATS_DTTM'] !== 'total' &&
              rowObj['PLTFOM_TP_CODE'] === undefined &&
              'faExternalLink'
            }
            toolTipTitle={t('해당 날짜의 컨버전리스트')}
            onClick={(e) => handlePortalUrl(showWindowUrl)}
          />
        );
      },
      orderFlag: false,
    },
    TRGT_ORDER_CNT: { merged: true },
    CONV_RATE: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'CONV_RATE').label}
            <br />({t('쇼핑')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CONV_RATE');
        const bodyItem2 = getHeadObj(headsMap, 'TRGT_CONV_RATE');
        return (
          <>
            {makeRowColumn(
              rowObj['CONV_RATE'],
              bodyItem1.type,
              bodyItem1.format
            )}
            <br />(
            {makeRowColumn(
              rowObj['TRGT_CONV_RATE'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      orderFlag: false,
    },
    TRGT_CONV_RATE: { merged: true },
    AVG_ORDER_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'AVG_ORDER_AMT').label}
            <br />({t('쇼핑')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'AVG_ORDER_AMT');
        const bodyItem2 = getHeadObj(headsMap, 'TRGT_AVG_ORDER_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['AVG_ORDER_AMT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            <br />(
            {makeRowColumn(
              rowObj['TRGT_AVG_ORDER_AMT'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      orderFlag: false,
    },
    TRGT_AVG_ORDER_AMT: { merged: true },
    CONV_AMT: {
      makeHead: (headsMap) => {
        return (
          <>
            {getHeadObj(headsMap, 'CONV_AMT').label}
            <br />({t('쇼핑')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CONV_AMT');
        const bodyItem2 = getHeadObj(headsMap, 'TRGT_CONV_AMT');
        return (
          <>
            {makeRowColumn(
              rowObj['CONV_AMT'],
              bodyItem1.type,
              bodyItem1.format
            )}
            <br />(
            {makeRowColumn(
              rowObj['TRGT_CONV_AMT'],
              bodyItem2.type,
              bodyItem2.format
            )}
            )
          </>
        );
      },
      orderFlag: false,
    },
    TRGT_CONV_AMT: { merged: true },
    MOB_SESSION_ROAS: {
      makeHead: (headsMap) => {
        return (
          <ShoppingCell
            type="head"
            headsMap={headsMap}
            itemKey="MOB_SESSION_ROAS"
          />
        );
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <ShoppingCell
            rowObj={rowObj}
            headsMap={headsMap}
            itemKey="MOB_SESSION_ROAS"
          />
        );
      },
      orderFlag: false,
    },
    TRGT_MOB_SESSION_ROAS: { merged: true },
    MOB_SESSION_ORDER_AMT: { merged: true },
    BROWSER_SESSION_ROAS: {
      makeHead: (headsMap) => {
        return (
          <ShoppingCell
            type="head"
            headsMap={headsMap}
            itemKey="BROWSER_SESSION_ROAS"
          />
        );
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <ShoppingCell
            rowObj={rowObj}
            headsMap={headsMap}
            itemKey="BROWSER_SESSION_ROAS"
          />
        );
      },
      orderFlag: false,
    },
    TRGT_BROWSER_SESSION_ROAS: { merged: true },
    BROWSER_SESSION_ORDER_AMT: { merged: true },
    SESSION_ROAS: {
      makeHead: (headsMap) => {
        return (
          <ShoppingCell
            type="head"
            headsMap={headsMap}
            itemKey="SESSION_ROAS"
          />
        );
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <ShoppingCell
            rowObj={rowObj}
            headsMap={headsMap}
            itemKey="SESSION_ROAS"
          />
        );
      },
      orderFlag: false,
    },
    TRGT_SESSION_ROAS: { merged: true },
    SESSION_ORDER_AMT: { merged: true },
    DIRECT_ROAS: {
      makeHead: (headsMap) => {
        return (
          <ShoppingCell type="head" headsMap={headsMap} itemKey="DIRECT_ROAS" />
        );
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <ShoppingCell
            rowObj={rowObj}
            headsMap={headsMap}
            itemKey="DIRECT_ROAS"
          />
        );
      },
      orderFlag: false,
    },
    TRGT_DIRECT_ROAS: { merged: true },
    DIRECT_ORDER_AMT: { merged: true },
    ROAS: {
      makeHead: (headsMap) => {
        return <ShoppingCell type="head" headsMap={headsMap} itemKey="ROAS" />;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <ShoppingCell rowObj={rowObj} headsMap={headsMap} itemKey="ROAS" />
        );
      },
      orderFlag: false,
    },
    TRGT_ROAS: { merged: true },
    ORDER_AMT: { merged: true },
    ECPM: {
      makeHead: (headsMap) => {
        return <ShoppingCell type="head" headsMap={headsMap} itemKey="ECPM" />;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <ShoppingCell rowObj={rowObj} headsMap={headsMap} itemKey="ECPM" />
        );
      },
      orderFlag: false,
    },
    TRGT_ECPM: { merged: true },
  };

  /**
   * 스낵바 리덕스 값을 변경해주는 함수
   * @type {(...args: any[]) => any}
   */
  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <NewFilterWrap loading={loading} handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={tempParams}
          getParam={getTempParam}
          pltfomTpCodeDetailFilter={['03', '04']}
          externalList={['01', '02', '98', '99']}
        />
        <NewButton type="filterButton" onClick={getParam} disableRipple>
          {t('검색')}
        </NewButton>
      </NewFilterWrap>
      {!isPopPage && (
        <StatisticsPieGraph
          params={paramConverter(params)}
          handleClickSnackbar={handleClickSnackbar}
        />
      )}

      <BarGraph
        params={params}
        dataReqPromise={dailyParGraph}
        graphList={graphList}
        title={t('일자별 데이터 그래프')}
        labelType="MMDD"
        useStackId={false}
      />
      <NewTableContainer
        params={params}
        useCustomTable={!isPopPage}
        dataReqPromise={!!isPopPage ? dailyStatistics : (f) => f}
        propsTotalList={tableList.total}
        propsList={tableList.list}
        propsHeadCells={makeHeadData(tableHead)}
        getParam={getParam}
        customized={customized}
        expandTable
        rowsPerPage={-1}
        useFetch={false}
        useSearch={false}
        usePagenation={false}
        loading={loading}
        useTotalSubList
      />
    </>
  );
};

export default withRouter(Statistics);
